import { validateAddress } from "../../helpers/addressValidation.mjs";
import { ALLOWED_TYPES, buildAutocompleteUrl, buildPlacesUrl, generateGoogleMapsSessionToken } from "../../helpers/googleMaps.mjs";
import AutocompleteInput from "../AutocompleteInput/AutocompleteInput.mjs";
import "../AutocompleteInput/index.mjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonLoadingAnimation, Icons, Text } from "@opendoor/bricks-next";
import debounce from "lodash/debounce";
import { jsx } from "react/jsx-runtime";
//#region src/core/AddressInput/AddressInput.tsx
var DEBOUNCE_INVOKE_WAIT_MS = 100;
/**
* @param actionButton overrides `icon` in the suffix position when provided
*/
var AddressInput = ({ id, placeholder, disabled, hideLabel, label, value, onChange, defaultValue, fetchOnExternalValue = false, onAddressSelect, onAddressValidated, validateAddress: validateAddress$1 = false, onError, onPredictionsChange, onSearchTermChange, onFocus, onBlur, customAction, listBoxId, listBoxAriaLabel, icon, actionButton, size, supportText, errorText, invalid, required }) => {
	const [predictions, setPredictions] = useState([]);
	const [usedPrediction, setUsedPrediction] = useState(null);
	const [internalSearchTerm, setInternalSearchTerm] = useState(defaultValue ?? "");
	const [isLoading, setIsLoading] = useState(false);
	const [sessionToken] = useState(() => generateGoogleMapsSessionToken());
	const inputRef = useRef(null);
	const didMountRef = useRef(false);
	const isControlled = value !== void 0;
	const searchTerm = isControlled ? value : internalSearchTerm;
	const updateSearchTerm = (newValue) => {
		if (!isControlled) setInternalSearchTerm(newValue);
		onChange?.(newValue);
	};
	useEffect(() => {
		onPredictionsChange?.(predictions);
	}, [predictions, onPredictionsChange]);
	useEffect(() => {
		if (!isControlled) onSearchTermChange?.(searchTerm);
	}, [
		isControlled,
		searchTerm,
		onSearchTermChange
	]);
	const clearPredictions = () => {
		setUsedPrediction(null);
		setPredictions([]);
	};
	const fetchAutocompleteOptions = useMemo(() => debounce(async (input) => {
		if (!input) {
			clearPredictions();
			return;
		}
		try {
			const data = await (await fetch(buildAutocompleteUrl(input, sessionToken))).json();
			data.predictions.forEach((result) => {
				result.description = result.description.replace(", USA", "");
				result.id = result.place_id || result.addressToken || result.description;
				result.ariaLabel = result.description;
			});
			setPredictions(data.predictions.filter((result) => {
				result.source = data.source;
				if (data.source === "google") {
					const terms = result.terms ?? [];
					const allowedTypes = (result.types ?? []).filter((type) => ALLOWED_TYPES.includes(type));
					return terms.length >= 5 && allowedTypes.length > 0 || terms.length === 4 && terms[0].value.match(/^\d+\S*\s\S+\s/) !== null;
				}
				return true;
			}));
		} catch (error) {
			clearPredictions();
			onError?.(error);
		}
	}, DEBOUNCE_INVOKE_WAIT_MS), [sessionToken, onError]);
	useEffect(() => {
		if (!fetchOnExternalValue) return;
		if (!didMountRef.current) {
			didMountRef.current = true;
			return;
		}
		if (searchTerm === "") clearPredictions();
		else fetchAutocompleteOptions(searchTerm);
	}, [
		fetchOnExternalValue,
		searchTerm,
		fetchAutocompleteOptions
	]);
	const handleInputChange = (e) => {
		const newValue = e.target.value;
		updateSearchTerm(newValue);
		fetchAutocompleteOptions(newValue);
		if (newValue === "") clearPredictions();
	};
	const handlePredictionSelect = async (prediction) => {
		inputRef.current?.blur();
		setUsedPrediction(prediction);
		updateSearchTerm(prediction.description);
		setPredictions([]);
		setIsLoading(true);
		const url = prediction.source === "google" ? buildPlacesUrl({
			source: "google",
			placeId: prediction.place_id,
			sessionToken
		}) : buildPlacesUrl({
			source: "casa",
			addressToken: prediction.addressToken,
			sessionToken
		});
		try {
			const address = await (await fetch(url)).json();
			if (validateAddress$1 && onAddressValidated) try {
				onAddressValidated(await validateAddress(address));
			} catch (validationError) {
				onError?.(validationError);
			}
			onAddressSelect(address);
		} catch (error) {
			onError?.(error);
		} finally {
			setIsLoading(false);
		}
	};
	const renderItem = (prediction) => {
		return /* @__PURE__ */ jsx(Text, {
			typography: size ? {
				xsmall: "$bodyMedium",
				small: "$bodyMedium",
				medium: "$bodyLarge",
				large: "$bodyXlarge"
			}[size] : "$bodyXlarge",
			tag: "span",
			textAlign: "left",
			$smallerThanMD: { typography: "$bodyMedium" },
			px: 10,
			py: 2,
			children: prediction.description
		});
	};
	let useSuffix = icon ?? (isLoading ? /* @__PURE__ */ jsx(Box, {
		position: "relative",
		px: "$4",
		display: "block",
		children: /* @__PURE__ */ jsx(ButtonLoadingAnimation, {
			variant: "secondary",
			size: "xsmall"
		})
	}) : /* @__PURE__ */ jsx(Icons.Search, {}));
	if (actionButton) {
		const onPress = () => {
			if (predictions.length && predictions[0]) handlePredictionSelect(predictions[0]);
			else if (usedPrediction && searchTerm === usedPrediction.description) handlePredictionSelect(usedPrediction);
		};
		useSuffix = actionButton(onPress, isLoading);
	}
	return /* @__PURE__ */ jsx(AutocompleteInput, {
		id,
		label,
		hideLabel,
		placeholder,
		disabled,
		items: predictions,
		searchTerm,
		onSearchChange: handleInputChange,
		onItemSelect: handlePredictionSelect,
		renderItem,
		suffix: useSuffix,
		customAction,
		listBoxId,
		onEscape: () => clearPredictions(),
		onFocus,
		onBlur,
		listBoxAriaLabel,
		size,
		supportText,
		errorText,
		invalid,
		required,
		inputRef
	});
};
//#endregion
export { AddressInput as default };

//# sourceMappingURL=AddressInput.mjs.map